import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Check, Building, ClipboardCopy, Users } from 'lucide-react';
import { getEnterpriseDetails } from '../services/enterprise';
import proxyapi from '../services/ProxyApi';


const EnterpriseSuccess = () => {
  
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [enterpriseDetails, setEnterpriseDetails] = useState(null);
  const [copied, setCopied] = useState(false);
  const [redirectCountdown, setRedirectCountdown] = useState(null);
  
  // Define startRedirectCountdown with useCallback so it can be used in the dependency array
  const startRedirectCountdown = useCallback(() => {
    let countdown = 5;
    setRedirectCountdown(countdown);
    
    const countdownInterval = setInterval(() => {
      countdown -= 1;
      setRedirectCountdown((prev) => prev - 1);
      
      if (countdown <= 0) {
        clearInterval(countdownInterval);
        navigate('/account', { state: { activeTab: 'enterprise' } });
      }
    }, 1000);
    
    // Return cleanup function
    return () => clearInterval(countdownInterval);
  }, [navigate]);

  useEffect(() => {
    // Extract the session_id from the URL query parameters
    const params = new URLSearchParams(location.search);
    const sessionId = params.get('session_id');

    if (!sessionId) {
      setError('Missing session information');
      setIsLoading(false);
      return;
    }

    const verifyPayment = async () => {
      try {
        // Verify payment first
        const response = await proxyapi.get(`/api/v1/payments/verify?session_id=${sessionId}`);
        
        if (response.data.status !== 'success') {
          throw new Error('Payment verification failed');
        }
        
        // Get the user ID for a more reliable API call
        const userId = response.data.userId;
        
        // Get enterprise details - we need to wait a moment for the webhook to process
        // and create the enterprise account
        setTimeout(async () => {
          try {
            // Try to get enterprise status directly using userId instead of 'current'
            const statusResponse = await proxyapi.get(`/api/v1/users/${userId}/enterprise-status`);
            
            if (statusResponse.data.status === 'success' && statusResponse.data.isEnterpriseMember) {
              // Now get the enterprise details
              const enterpriseResponse = await getEnterpriseDetails(statusResponse.data.enterpriseId);
              
              if (enterpriseResponse && enterpriseResponse.status === 'success') {
                setEnterpriseDetails(enterpriseResponse.enterprise);
                setIsLoading(false);
              } else {
                throw new Error('Failed to fetch enterprise details');
              }
            } else {
              // If no enterprise status yet, wait a bit longer and try one more time
              setTimeout(async () => {
                try {
                  const retryStatusResponse = await proxyapi.get(`/api/v1/users/${userId}/enterprise-status`);
                  
                  if (retryStatusResponse.data.status === 'success' && retryStatusResponse.data.isEnterpriseMember) {
                    const retryEnterpriseResponse = await getEnterpriseDetails(retryStatusResponse.data.enterpriseId);
                    
                    if (retryEnterpriseResponse && retryEnterpriseResponse.status === 'success') {
                      setEnterpriseDetails(retryEnterpriseResponse.enterprise);
                      setIsLoading(false);
                      return;
                    }
                  }
                  
                  // If we still don't have the enterprise details, show fallback
                  setError('Enterprise account created, but details not available yet.');
                  startRedirectCountdown();
                  setIsLoading(false);
                } catch (err) {
                  console.error('Error in retry fetch:', err);
                  setError('Enterprise account created, but details could not be loaded.');
                  startRedirectCountdown();
                  setIsLoading(false);
                }
              }, 3000); // Wait 3 more seconds for a retry
            }
          } catch (err) {
            console.error('Error fetching enterprise details:', err);
            setError('Enterprise account created, but details could not be loaded.');
            startRedirectCountdown();
            setIsLoading(false);
          }
        }, 2000); // Initial wait of 2 seconds
        
      } catch (err) {
        console.error('Error verifying enterprise payment:', err);
        setError('Could not verify enterprise account creation.');
        setIsLoading(false);
      }
    };

    verifyPayment();
  }, [location.search, startRedirectCountdown]);

  const handleCopyKey = () => {
    if (!enterpriseDetails?.enterpriseKey) return;
    
    navigator.clipboard.writeText(enterpriseDetails.enterpriseKey);
    setCopied(true);
    
    setTimeout(() => {
      setCopied(false);
    }, 3000);
  };

  const handleContinue = () => {
    navigate('/account', { state: { activeTab: 'enterprise' } });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto mb-4"></div>
          <p className="text-gray-700">Setting up your enterprise account...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="bg-white p-8 rounded-lg shadow-md max-w-md w-full text-center">
          <div className="bg-yellow-50 p-6 rounded-lg mb-6">
            <div className="flex items-center justify-center mb-4">
              <Check className="h-8 w-8 text-green-600 mr-2" />
              <h2 className="text-xl font-semibold text-green-800">Payment Successful</h2>
            </div>
            <p className="text-gray-700 mb-2">Your enterprise account has been created.</p>
            <p className="text-gray-600 text-sm">{error}</p>
            
            {redirectCountdown !== null && (
              <p className="mt-3 text-sm text-gray-600">
                Redirecting to your account in <span className="font-bold">{redirectCountdown}</span> seconds...
              </p>
            )}
          </div>
          
          <button
            onClick={handleContinue}
            className="bg-black text-white px-6 py-2 rounded hover:bg-gray-800"
          >
            Go to Account Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-lg mx-auto bg-white rounded-lg shadow-md p-8">
        <div className="text-center mb-6">
          <div className="bg-green-100 rounded-full p-3 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
            <Check className="h-8 w-8 text-green-600" />
          </div>
          <h1 className="text-2xl font-bold mb-2">Enterprise Account Created!</h1>
          <p className="text-gray-600">
            Your enterprise account has been successfully set up.
          </p>
        </div>

        {enterpriseDetails && (
          <div className="mb-8">
            <div className="bg-gray-50 p-4 rounded-md mb-6">
              <h2 className="font-semibold text-lg mb-3 flex items-center">
                <Building className="w-5 h-5 mr-2 text-gray-700" />
                Enterprise Details
              </h2>
              
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Name</label>
                  <p className="mt-1 font-medium">{enterpriseDetails.name}</p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">Seats</label>
                  <p className="mt-1">
                    <span className="font-medium">{enterpriseDetails.usedSeats || 1}</span> of{' '}
                    <span className="font-medium">{enterpriseDetails.seatLimit}</span> used
                  </p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">Enterprise Key</label>
                  <div className="mt-1 flex items-center">
                    <code className="text-sm bg-gray-100 p-2 rounded flex-grow font-mono">
                      {enterpriseDetails.enterpriseKey}
                    </code>
                    <button
                      onClick={handleCopyKey}
                      className="ml-2 p-2 text-gray-600 hover:text-black bg-white rounded-md border border-gray-200"
                      title="Copy enterprise key"
                    >
                      {copied ? (
                        <Check className="h-5 w-5 text-green-500" />
                      ) : (
                        <ClipboardCopy className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                  <p className="text-sm text-gray-500 mt-1">
                    Share this key with your team members to let them join
                  </p>
                </div>
              </div>
            </div>
            
            <div className="bg-blue-50 p-4 rounded-md mb-6 border border-blue-100">
              <h3 className="font-medium text-blue-800 mb-2 flex items-center">
                <Users className="w-5 h-5 mr-2" />
                Next Steps
              </h3>
              <ul className="text-blue-700 text-sm space-y-1 ml-7 list-disc">
                <li>Share your enterprise key with team members</li>
                <li>Team members can join using the key in their account settings</li>
                <li>Manage team members from your Enterprise dashboard</li>
              </ul>
            </div>
          </div>
        )}

        <div className="text-center">
          <button
            onClick={handleContinue}
            className="px-6 py-2 bg-black text-white rounded-md hover:bg-gray-800"
          >
            Go to Enterprise Dashboard
          </button>
        </div>
      </div>
    </div>
  );
};

export default EnterpriseSuccess;